// src/components/VariantDialog.jsx
import * as React from 'react';
import {
	Dialog, DialogTitle, DialogContent, DialogActions,
	Grid, FormLabel, IconButton, Select, MenuItem, TextField,
	Button, Typography, Divider, InputAdornment, Chip, CardActionArea, Box
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

import {
	COLOR_NAMES, SIZE_CATEGORIES,
	hexToRgb, getNearestColorName
} from './helpers';
import {ThemeProvider} from "@mui/material/styles";
import {theme, buttonTheme} from "../../theme";

const buildVariantCode = ({colorVariation, sizeVariation}) =>
	[colorVariation, sizeVariation].filter(Boolean).join(" • ") || "Untitled";

/* ——— swatch palette (same as product picker) ——— */
const SWATCHES = [
	'#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981', '#14b8a6',
	'#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899',
	'#f43f5e', '#000000', '#374151', '#6b7280', '#9ca3af', '#d1d5db', '#e5e7eb', '#ffffff'
];

/* ——— main component ——— */
function VariantDialogRaw({
	                          open, onClose,
	                          isEdit = false,
	                          variation,
	                          onSave,
	                          onChange,
	                          handlers, inputRefs, images, removeImage
                          }) {
	/* —— local state for size tabs —— */
	const firstCat = Object.keys(SIZE_CATEGORIES)[0];
	const firstSub = Object.keys(SIZE_CATEGORIES[firstCat])[0];

	const [cat, setCat] = React.useState(firstCat);
	const [sub, setSub] = React.useState(firstSub);

	const [confirmClose, setConfirmClose] = React.useState(false);

	/* keep sub-category valid when cat changes */
	React.useEffect(() => {
		if (!SIZE_CATEGORIES[cat][sub])
			setSub(Object.keys(SIZE_CATEGORIES[cat])[0]);
	}, [cat, sub]);

	const numberFieldProps = {
		type: 'number',
		inputProps: {min: 0},                 // native min guard
		onWheel: (e) => e.currentTarget.blur(), // block scroll-to-change
		onKeyDown: (e) => {                     // block -, +, e/E
			if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
		},
	};

	// clamp and forward as a normal onChange event for parent handler
	const clampNonNeg = (name) => (e) => {
		const raw = e.target.value;
		const value = raw === '' ? '' : Math.max(0, Number(raw));
		onChange({target: {name, value}});
	};

	const handleColourPick = (hex) => {
		const nearest = getNearestColorName(hex);
		const {r, g, b} = hexToRgb(hex);
		onChange({target: {name: 'colorVariation', value: nearest}});
		onChange({target: {name: 'color', value: `rgb(${r}, ${g}, ${b})`}});
		onChange({target: {name: 'colorHex', value: hex}});
	};

	/* —— helpers for progressive image list —— */
	const varImgs = [images.variantPrimary, images.variantSec1, images.variantSec2, images.variantSec3, images.variantSec4];
	const inps = [inputRefs.variantInput1, inputRefs.variantInput2, inputRefs.variantInput3, inputRefs.variantInput4, inputRefs.variantInput5];
	const ups = [handlers.handleVariantPrimaryUpload, handlers.handleVariantSec1Upload, handlers.handleVariantSec2Upload, handlers.handleVariantSec3Upload, handlers.handleVariantSec4Upload];

	const requiredOk = [
			'variationPricePerUnit',
			'variationUnits',
			'variationQuantity',
			'variationBarCode',
			'variationSKU',
			'variationMaxStock',
			'variationInStock',
			'variationLowStock'
		].every(key => variation[key] !== '' && variation[key] !== null && variation[key] !== undefined)
		&& !!images.variantPrimary;   // image still mandatory


	const renderImg = (idx) => (
		<Grid item xs={6} sm={4} md={2.4} key={idx}>
			{varImgs[idx]
				? (<Box position="relative">
					<CardActionArea sx={{border: '2px dashed', borderRadius: 2, height: 96, bgcolor: '#fff'}} onClick={() => window.open(varImgs[idx], '_blank')}>
						<Box component="img" src={varImgs[idx]} sx={{width: '100%', height: 90, objectFit: 'cover', borderRadius: 1}}/>
					</CardActionArea>
					<IconButton size="small" sx={{position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,.7)'}} onClick={() => removeImage(idx)}>
						<CloseIcon fontSize="small"/>
					</IconButton>
					{idx === 0 && <Chip label="Primary" size="small" color="primary" sx={{position: 'absolute', bottom: 2, left: 2}}/>}
				</Box>)
				: (<CardActionArea sx={{display: 'flex', justifyContent: 'center', border: '2px dashed', borderRadius: 2, height: 96, bgcolor: '#fff'}} onClick={() => inps[idx].current?.click()}>
					<AddIcon/>
					<input hidden accept="image/*" type="file" ref={inps[idx]} onChange={ups[idx]}/>
				</CardActionArea>)
			}
		</Grid>
	);

	return (
		<Dialog
			fullWidth
			maxWidth="lg"
			open={open}
			disableEscapeKeyDown
			keepMounted
			disableEnforceFocus
			onClose={(e, reason) => {
				if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
					setConfirmClose(true);
					return;
				}
				onClose();
			}}
		>
			<ThemeProvider theme={theme}>
				<DialogTitle>{isEdit ? 'Edit Variant' : 'Add Variant'}</DialogTitle>

				<DialogContent
					dividers
					sx={{
						maxHeight: '90vh',
						overflow: 'auto',
						'& input[type=number]': {MozAppearance: 'textfield'},
						'& input[type=number]::-webkit-outer-spin-button': {WebkitAppearance: 'none', margin: 0},
						'& input[type=number]::-webkit-inner-spin-button': {WebkitAppearance: 'none', margin: 0},
					}}
				>
					<Grid container spacing={3}>

						{/* ─── Colour ─────────────────────────────── */}
						<Grid item xs={12} md={6}>
							<FormLabel>Colour</FormLabel>
							<Grid container spacing={1} sx={{mt: 1, maxWidth: 280}}>
								{SWATCHES.map(hex => (
									<Grid item xs={2} key={hex}>
										<IconButton size="small" onClick={() => handleColourPick(hex)}
										            sx={{
											            width: 32, height: 32, borderRadius: 1, bgcolor: hex,
											            border: variation.colorHex === hex ? '2px solid #2563eb' : '1px solid #e5e7eb',
											            transform: variation.colorHex === hex ? 'scale(1.15)' : 'none',
											            transition: 'all .15s'
										            }}/>
									</Grid>
								))}
								{/* custom */}
								<Grid item xs={4}>
									<input type="color" value={variation.colorHex || '#000000'} onChange={e => handleColourPick(e.target.value)}
									       style={{width: '100%', height: 32, border: 'none', padding: 0}}/>
								</Grid>
							</Grid>
							{variation.colorVariation && <Typography variant="caption" sx={{mt: .5, display: 'block'}}>Selected: {variation.colorVariation}</Typography>}
						</Grid>

						{/* ─── Size ──────────────────────────────── */}
						<Grid item xs={12} md={6}>
							<FormLabel>Size</FormLabel>
							<Grid container spacing={2} sx={{mt: 1, mb: 1}}>
								<Grid item xs={6}>
									<Select fullWidth value={cat} onChange={e => setCat(e.target.value)}>
										{Object.keys(SIZE_CATEGORIES).map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
									</Select>
								</Grid>
								<Grid item xs={6}>
									<Select fullWidth value={sub} onChange={e => setSub(e.target.value)}>
										{Object.keys(SIZE_CATEGORIES[cat]).map(sc => <MenuItem key={sc} value={sc}>{sc}</MenuItem>)}
									</Select>
								</Grid>
							</Grid>

							<Grid container spacing={1}>
								{(SIZE_CATEGORIES[cat] && SIZE_CATEGORIES[cat][sub] ? SIZE_CATEGORIES[cat][sub] : []).map(sz => (
									<Grid item key={sz}>
										<Button sx={{fontWeight:'bold'}} color='secondary' variant={variation.sizeVariation === sz ? 'contained' : 'outlined'} size="small"
										        onClick={() => onChange({target: {name: 'sizeVariation', value: sz}})}>{sz}</Button>
									</Grid>
								))}
							</Grid>
							{variation.sizeVariation && <Typography variant="caption" sx={{mt: .5, display: 'block'}}>Selected: {variation.sizeVariation}</Typography>}
						</Grid>

						{/* ─── Inventory header ───────────────────── */}
						<Grid item xs={12}><Divider/></Grid>
						<Grid item xs={12}>
							<Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>Inventory</Typography>
							<Grid container spacing={2}>
								{[
									{lab: 'Max Stock *', name: 'variationMaxStock'},
									{lab: 'Current Stock *', name: 'variationInStock'},
									{lab: 'Low Stock *', name: 'variationLowStock'}
								].map(({lab, name}) => (
									<Grid item xs={12} sm={4} key={name}>
										<TextField
											size="small"
											fullWidth
											label={lab}
											name={name}
											{...numberFieldProps}
											value={variation[name] ?? ''}
											onChange={clampNonNeg(name)}
										/>
									</Grid>
								))}
							</Grid>
						</Grid>

						{/* ─── Pricing header ─────────────────────── */}
						<Grid item xs={12}><Divider/></Grid>
						<Grid item xs={12}>
							<Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>Pricing</Typography>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={3}>
									<TextField
										size="small"
										fullWidth
										label="Price / Unit *"
										name="variationPricePerUnit"
										{...numberFieldProps}
										value={variation.variationPricePerUnit ?? ''}
										onChange={clampNonNeg('variationPricePerUnit')}
										InputProps={{startAdornment: <InputAdornment position="start">₹</InputAdornment>}}
									/>
								</Grid>
								<Grid item xs={12} sm={3}>
									<TextField
										size="small"
										fullWidth
										label="Units *"
										name="variationUnits"
										{...numberFieldProps}
										value={variation.variationUnits ?? ''}
										onChange={clampNonNeg('variationUnits')}
									/>
								</Grid>
								<Grid item xs={12} sm={3}>
									<TextField
										size="small"
										fullWidth
										label="Quantity *"
										name="variationQuantity"
										{...numberFieldProps}
										value={variation.variationQuantity ?? ''}
										onChange={clampNonNeg('variationQuantity')}
									/>
								</Grid>
								<Grid item xs={12} sm={3}>
									<TextField size="small" fullWidth label="Total Price" name="variationTotalPrice" disabled
									           value={variation.variationTotalPrice}
									           InputProps={{startAdornment: <InputAdornment position="start">₹</InputAdornment>}}/>
								</Grid>
								<Grid item xs={12} sm={3}>
									<TextField
										size="small"
										fullWidth
										label="Barcode *"
										name="variationBarCode"
										value={variation.variationBarCode}
										onChange={onChange}
									/>
								</Grid>
								<Grid item xs={12} sm={3}>
									<TextField
										size="small"
										fullWidth
										label="SKU *"
										name="variationSKU"
										value={variation.variationSKU}
										onChange={onChange}
									/>
								</Grid>
							</Grid>
						</Grid>

						{/* ─── Images ─────────────────────────────── */}
						<Grid item xs={12}><Divider/></Grid>
						<Grid item xs={12}>
							<Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>Images</Typography>
							<Grid container spacing={2}>
								{Array.from({length: Math.min(varImgs.filter(Boolean).length + 1, 5)}).map((_, i) => renderImg(i))}
							</Grid>
						</Grid>
					</Grid>
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose} color="secondary">Cancel</Button>
					<Button
						variant="contained"
						color="primary"
						disabled={!requiredOk}
						onClick={() => {
							const ready = {
								...variation,
								variantCode: buildVariantCode(variation),
								inStock: Number(variation.variationInStock),
								totalPrice: Number(variation.variationTotalPrice),
							};
							onSave(ready);
						}}
					>
						{isEdit ? "Update" : "Add"} Variant
					</Button>
				</DialogActions>
				<Dialog
					open={confirmClose}
					onClose={() => setConfirmClose(false)}
				>
					<DialogTitle>Discard changes?</DialogTitle>
					<DialogActions>
						<Button onClick={() => setConfirmClose(false)} color="secondary">Keep editing</Button>
						<Button
							color="error"
							onClick={() => {
								setConfirmClose(false);
								onClose();            // really close parent dialog
							}}
						>
							Discard
						</Button>
					</DialogActions>
				</Dialog>
			</ThemeProvider>
		</Dialog>
	);
}

/* export a memoised version so it never re-mounts unnecessarily */
export const VariantDialog = React.memo(VariantDialogRaw);
export default VariantDialog;
